import { useCallback, useEffect, useState } from 'react';

import { getOrders } from '@/modules/orders/redux/OrderCRUD';

import { RequestMaterialOrderOption } from '../types';

const mapOrderToOption = (order: any): RequestMaterialOrderOption => ({
  id: order.id,
  order_no: order.code || '',
  name: order.name || order.customer_name || order.code || '',
  quantity: order.quantity ?? undefined,
  unit_name: order.unit_name || undefined,
});

interface UseRequestMaterialOrdersParams {
  companyId: string;
  search?: string;
  skip?: boolean;
  onError?: (message: string) => void;
}

export default function useRequestMaterialOrders({
  companyId,
  search = '',
  skip = false,
  onError,
}: UseRequestMaterialOrdersParams) {
  const [orderOptions, setOrderOptions] = useState<RequestMaterialOrderOption[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchOrders = useCallback(async (company: string, searchValue = '') => {
    if (!company || company === 'global') {
      setOrderOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await getOrders({ company, search: searchValue.trim() || undefined, page_size: 50 });
      const results: any[] = response.data?.results || response.data || [];
      setOrderOptions(results.map(mapOrderToOption));
    } catch (_error) {
      onError?.('Không thể tải danh sách đơn hàng.');
      setOrderOptions([]);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    if (skip || !companyId) return;
    const debounceTimer = window.setTimeout(() => {
      void fetchOrders(companyId, search);
    }, 300);

    return () => {
      window.clearTimeout(debounceTimer);
    };
  }, [companyId, fetchOrders, search, skip]);

  const reload = () => void fetchOrders(companyId, search);

  return { orderOptions, loading, reload };
}
